<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import teamVincent from '@/assets/marketing/team-vincent.webp'

const props = withDefaults(defineProps<{ variant?: 'public' | 'dashboard' }>(), { variant: 'public' })

interface TeamMember {
  key: 'founder'
  initials: string
  // Square portrait (round artwork on a transparent background); members without one show their initials.
  photo?: string
}

// Adding a member is a constant + i18n change (name, role, quote, fact and, with a photo, photo_alt).
const TEAM_MEMBERS: ReadonlyArray<TeamMember> = [{ key: 'founder', initials: 'V', photo: teamVincent }]

const { t } = useI18n()
const isDashboard = computed(() => props.variant === 'dashboard')
</script>

<template>
  <section
    :class="isDashboard ? '' : 'bg-white py-24'"
    aria-labelledby="about-team-title"
  >
    <div :class="isDashboard ? '' : 'max-w-5xl mx-auto px-4 sm:px-6 lg:px-8'">
      <div class="text-center mb-12">
        <p class="font-semibold text-sm uppercase tracking-widest" :class="isDashboard ? 'text-emerald-400' : 'text-emerald-600'">
          {{ t('about.team.eyebrow') }}
        </p>
        <h2
          id="about-team-title"
          class="text-3xl md:text-4xl font-bold mt-3"
          :class="isDashboard ? 'text-white' : 'text-slate-900'"
        >
          {{ t('about.team.title') }}
        </h2>
      </div>

      <div
        v-for="member in TEAM_MEMBERS"
        :key="member.key"
        class="grid grid-cols-1 md:grid-cols-3 gap-8 items-center rounded-2xl p-8 border"
        :class="isDashboard ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-100'"
      >
        <img
          v-if="member.photo"
          :src="member.photo"
          width="1254"
          height="1254"
          :alt="t(`about.team.members.${member.key}.photo_alt`)"
          loading="lazy"
          class="aspect-square max-w-48 w-full h-auto mx-auto rounded-full object-cover"
        />
        <div
          v-else
          class="aspect-square max-w-48 w-full mx-auto rounded-2xl bg-emerald-500 flex items-center justify-center"
          aria-hidden="true"
        >
          <span class="text-6xl font-black text-white">{{ member.initials }}</span>
        </div>
        <div class="md:col-span-2">
          <p class="text-xl font-bold" :class="isDashboard ? 'text-white' : 'text-slate-900'">
            {{ t(`about.team.members.${member.key}.name`) }}
          </p>
          <p class="text-sm font-semibold text-emerald-500">
            {{ t(`about.team.members.${member.key}.role`) }}
          </p>
          <blockquote class="mt-4 text-xl italic leading-relaxed" :class="isDashboard ? 'text-slate-300' : 'text-slate-700'">
            {{ t(`about.team.members.${member.key}.quote`) }}
          </blockquote>
          <p class="mt-4 text-sm" :class="isDashboard ? 'text-slate-400' : 'text-slate-500'">
            {{ t(`about.team.members.${member.key}.fact`) }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>
